import { toast } from 'sonner'

/** Standard durations by severity */
export const TOAST_DURATION = {
  success: 5000,
  error: 7000,
  undo: 5000,
} as const

/**
 * Wraps an async operation with loading → success/error toasts.
 * Returns the resolved value on success, or undefined on failure.
 *
 * For operations with complex branching (export with partial failures),
 * use manual toast calls instead.
 */
export async function toastAsync<T>(
  promise: Promise<T>,
  messages: {
    loading: string
    success: string | ((data: T) => string)
    error: string | ((err: unknown) => string)
    /** Optional description shown beneath the success message */
    successDescription?: string | ((data: T) => string)
  },
): Promise<T | undefined> {
  const toastId = toast.loading(messages.loading)

  try {
    const result = await promise
    const successMsg = typeof messages.success === 'function'
      ? messages.success(result)
      : messages.success
    const desc = typeof messages.successDescription === 'function'
      ? messages.successDescription(result)
      : messages.successDescription

    toast.success(successMsg, {
      id: toastId,
      duration: TOAST_DURATION.success,
      ...(desc ? { description: desc } : {}),
    })
    return result
  } catch (e) {
    const errorMsg = typeof messages.error === 'function'
      ? messages.error(e)
      : messages.error

    toast.error(errorMsg, {
      id: toastId,
      duration: TOAST_DURATION.error,
      description: e instanceof Error ? e.message : undefined,
    })
    return undefined
  }
}
